import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useSelector } from 'react-redux';
import { WEBSOCKET_CONFIG } from '../constants/interegation-variebles';
import { useUtility } from '../context/UtilityContext';
import { WebRTCService } from '../view/WcaEnqChat/webRTC';

const CallContext = createContext(undefined);

const RING_TIMEOUT_MS = 45000;
const TERMINAL_STATUSES = new Set(['failed', 'busy', 'no-answer', 'missed', 'disconnected']);
const DEVELOPMENT_MODE = process.env.NODE_ENV === 'development';

const EMPTY_ACTIVE_CALL = {
  isOpen: false,
  direction: null,
  phone: '',
  callId: null,
  callData: undefined,
  status: 'idle',
  error: '',
  startedAt: null,
  endedAt: null,
  endedReason: '',
};

const sanitizePhone = (value = '') => String(value).replace(/\D/g, '');

const getSourceObjects = (data = {}) => {
  const nestedCall = data?.payload?.entry?.[0]?.changes?.[0]?.value?.calls?.[0];
  const nestedDataCall = data?.data?.payload?.entry?.[0]?.changes?.[0]?.value?.calls?.[0];
  const payload = data?.payload;
  const response = data?.response;
  const message = data?.message;
  const dataMessage = data?.data?.message;
  const dataNode = data?.data;

  return [data, dataNode, payload, response, message, dataMessage, nestedCall, nestedDataCall].filter(Boolean);
};

const logCallDebug = (label, payload) => {
  if (!DEVELOPMENT_MODE) return;
  console.log(`[WCA Call] ${label}`, payload);
};

const getFirstValue = (sources = [], selectors = []) => {
  for (const source of sources) {
    for (const selector of selectors) {
      const value = selector(source);
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
  }

  return null;
};

const normalizeCallData = (data = {}) => {
  const sources = getSourceObjects(data);
  const phone = sanitizePhone(
    getFirstValue(sources, [
      (item) => item.phone,
      (item) => item.callerPhone,
      (item) => item.to,
      (item) => item.from,
    ]) || '',
  );
  const event = String(getFirstValue(sources, [(item) => item.event, (item) => item.type]) || '')
    .toLowerCase()
    .replace(/_/g, '-');
  const status = String(getFirstValue(sources, [(item) => item.status]) || '')
    .toLowerCase()
    .replace(/_/g, '-');
  const direction = getFirstValue(sources, [(item) => item.direction]);
  const reason = getFirstValue(sources, [(item) => item.reason, (item) => item.error]);

  const normalized = {
    raw: data,
    phone,
    callId: getFirstValue(sources, [
      (item) => item.callId,
      (item) => item.id,
      (item) => item.msgId,
      (item) => item.message?.id,
      (item) => item.session?.id,
      (item) => item.session?.callId,
    ]),
    msgId: getFirstValue(sources, [
      (item) => item.msgId,
      (item) => item.message?.id,
      (item) => item.id,
      (item) => item.callId,
      (item) => item.session?.id,
      (item) => item.session?.callId,
    ]),
    answerSdp: getFirstValue(sources, [
      (item) => item.sdpAnswer,
      (item) => item.answerSdp,
      (item) => item.payload?.answerSdp,
      (item) => item.payload?.sdpAnswer,
      (item) => item.response?.answerSdp,
      (item) => item.response?.sdpAnswer,
      (item) => item.sdp,
      (item) => item.sessionSdp,
      (item) => item.session?.sdpAnswer,
      (item) => item.session?.answerSdp,
      (item) => item.payload?.session?.sdp,
      (item) => item.response?.session?.sdp,
      (item) => item.session?.sdp,
    ]),
    sessionSdp: getFirstValue(sources, [
      (item) => item.sessionSdp,
      (item) => item.session?.sdp,
      (item) => item.payload?.session?.sdp,
      (item) => item.response?.session?.sdp,
      (item) => item.sdp,
    ]),
    sessionType: String(
      getFirstValue(sources, [
        (item) => item.session?.sdp_type,
        (item) => item.session?.type,
        (item) => item.sdp_type,
        (item) => item.type,
      ]) || '',
    ).toLowerCase(),
    event,
    status,
    direction,
    reason,
  };

  logCallDebug('normalized call data', normalized);
  return normalized;
};

const getCallPhone = (data = {}) => normalizeCallData(data).phone;
const getCallId = (data = {}) => normalizeCallData(data).callId;
const getOfferSdp = (data = {}) => {
  const normalized = normalizeCallData(data);
  if (normalized.sessionType === 'answer') {
    return null;
  }

  return normalized.sessionSdp || normalized.answerSdp || null;
};

const getRemoteAnswerSdp = (data = {}) => {
  const normalized = normalizeCallData(data);
  if (normalized.sessionType === 'offer') {
    return null;
  }

  const remoteAnswerSdp = normalized.answerSdp || normalized.sessionSdp || null;
  logCallDebug('remote answer SDP extraction', {
    sessionType: normalized.sessionType,
    hasAnswerSdp: Boolean(normalized.answerSdp),
    hasSessionSdp: Boolean(normalized.sessionSdp),
    hasRemoteAnswerSdp: Boolean(remoteAnswerSdp),
  });
  return remoteAnswerSdp;
};

const getEventStatus = (data = {}) => {
  const normalized = normalizeCallData(data);
  const eventStatus = normalized.event || normalized.status;

  if (eventStatus === 'busy') return 'busy';
  if (['timeout', 'no-answer'].includes(eventStatus)) return 'no-answer';
  if (eventStatus === 'missed') return 'missed';
  if (
    [
      'reject',
      'rejected',
      'terminate',
      'terminated',
      'end',
      'ended',
      'disconnect',
      'disconnected',
      'completed',
    ].includes(eventStatus)
  ) {
    return 'disconnected';
  }
  if (['failed', 'error'].includes(eventStatus)) return 'failed';
  return '';
};

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
};

export const CallProvider = ({ children }) => {
  const [activeCall, setActiveCall] = useState(EMPTY_ACTIVE_CALL);
  const callSocketRef = useRef(null);
  const activeCallRef = useRef(EMPTY_ACTIVE_CALL);
  const webRtcRef = useRef(null);
  const timeoutRef = useRef(null);
  const { selectedMetadataId } = useUtility();
  const { user } = useSelector((state) => state.authenticate);
  const companyId = user?.companyId;

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  const clearRingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const disconnectPeer = useCallback(() => {
    if (webRtcRef.current) {
      webRtcRef.current.disconnect();
      webRtcRef.current = null;
    }
  }, []);

  const updateActiveCall = useCallback((patch) => {
    setActiveCall((prev) => {
      const nextPatch = typeof patch === 'function' ? patch(prev) : patch;
      const nextState = { ...prev, ...nextPatch };
      if (nextState.callData && !nextState.callId) {
        nextState.callId = getCallId(nextState.callData);
      }
      if (!nextState.phone && nextState.callData) {
        nextState.phone = getCallPhone(nextState.callData);
      }
      return nextState;
    });
  }, []);

  const clearActiveCall = useCallback(() => {
    clearRingTimeout();
    disconnectPeer();
    setActiveCall(EMPTY_ACTIVE_CALL);
  }, [clearRingTimeout, disconnectPeer]);

  const matchesActiveCall = useCallback((data = {}, current = activeCallRef.current) => {
    if (!current?.isOpen) return false;

    const normalizedData = normalizeCallData(data);
    const currentCall = normalizeCallData(current.callData || {});
    const eventPhone = normalizedData.phone;
    const eventCallId = normalizedData.callId;
    const eventMsgId = normalizedData.msgId;
    const currentPhone = sanitizePhone(current.phone);
    const currentCallId = current.callId || currentCall.callId;
    const currentMsgId =
      current.callData?.msgId ||
      current.callData?.message?.id ||
      currentCall.msgId ||
      currentCallId;
    let matches = false;

    if (currentCallId && eventCallId) {
      matches = currentCallId === eventCallId;
    } else if (currentMsgId && eventMsgId) {
      matches = currentMsgId === eventMsgId;
    } else {
      matches = !!currentPhone && !!eventPhone && currentPhone === eventPhone;
    }

    logCallDebug('matchesActiveCall', {
      currentCallId,
      currentMsgId,
      currentPhone,
      eventCallId,
      eventMsgId,
      eventPhone,
      matches,
    });
    return matches;
  }, []);

  const ensurePeerConnection = async () => {
    if (!webRtcRef.current) {
      webRtcRef.current = new WebRTCService();
      await webRtcRef.current.initializePeerConnection();
    }

    return webRtcRef.current;
  };

  const createOutgoingOffer = async () => {
    const peer = await ensurePeerConnection();
    return peer.createOffer();
  };

  const createIncomingAnswer = async (offerSdp) => {
    const peer = await ensurePeerConnection();
    return peer.createAnswer(offerSdp);
  };

  const applyRemoteAnswer = useCallback(async (answerSdp) => {
    if (!answerSdp || !webRtcRef.current) return;
    try {
      await webRtcRef.current.handleAnswer(answerSdp);
      logCallDebug('applyRemoteAnswer success', { hasAnswerSdp: Boolean(answerSdp) });
    } catch (error) {
      logCallDebug('applyRemoteAnswer failure', error);
      throw error;
    }
  }, []);

  const markCallEnded = useCallback(
    ({ status = 'disconnected', error = '', reason = '', data = null } = {}) => {
      clearRingTimeout();
      disconnectPeer();
      updateActiveCall((prev) => ({
        isOpen: true,
        status,
        error,
        endedReason: reason || prev.endedReason,
        endedAt: prev.endedAt || Date.now(),
        callData: data ? { ...(prev.callData || {}), ...(data || {}) } : prev.callData,
      }));
    },
    [clearRingTimeout, disconnectPeer, updateActiveCall],
  );

  const markCallFailed = useCallback(
    (error) => {
      markCallEnded({
        status: 'failed',
        error: typeof error === 'string' ? error : error?.message || 'Call failed',
        reason: 'call-failed',
      });
    },
    [markCallEnded],
  );

  const startOutgoingCall = useCallback(({ phone, callData } = {}) => {
    const normalizedPhone = sanitizePhone(phone);
    setActiveCall({
      isOpen: true,
      direction: 'outgoing',
      phone: normalizedPhone,
      callId: getCallId(callData),
      callData,
      status: 'initiating',
      error: '',
      startedAt: null,
      endedAt: null,
      endedReason: '',
    });
  }, []);

  const openIncomingCall = useCallback((callData) => {
    setActiveCall({
      isOpen: true,
      direction: 'incoming',
      phone: getCallPhone(callData),
      callId: getCallId(callData),
      callData,
      status: 'incoming',
      error: '',
      startedAt: null,
      endedAt: null,
      endedReason: '',
    });
  }, []);

  const emitCallEvent = (eventName, payload) => {
    callSocketRef.current?.emit?.(eventName, payload);
  };

  const getSocketId = useCallback(() => callSocketRef.current?.id || '', []);

  const handleConnectedCall = useCallback(
    (data) => {
      if (!matchesActiveCall(data)) return;

      const normalized = normalizeCallData(data);
      const currentDirection = activeCallRef.current?.direction;
      const currentStatus = activeCallRef.current?.status;

      if (currentDirection === 'incoming') {
        if (currentStatus === 'incoming') {
          updateActiveCall((prev) => ({
            callId: prev.callId || normalized.callId,
            callData: { ...(prev.callData || {}), ...(data || {}) },
          }));
          return;
        }

        if (currentStatus !== 'connecting' && currentStatus !== 'connected') {
          return;
        }

        clearRingTimeout();
        updateActiveCall((prev) => ({
          status: 'connected',
          error: '',
          callId: prev.callId || normalized.callId,
          startedAt: prev.startedAt || Date.now(),
          endedAt: null,
          endedReason: '',
          callData: { ...(prev.callData || {}), ...(data || {}) },
        }));
        return;
      }

      const remoteAnswerSdp = getRemoteAnswerSdp(data);
      if (!remoteAnswerSdp) {
        const errorMessage = 'Remote answer SDP missing for outbound call connection';
        console.error(errorMessage, data);
        markCallFailed(errorMessage);
        return;
      }

      Promise.resolve(applyRemoteAnswer(remoteAnswerSdp))
        .then(() => {
          clearRingTimeout();
          updateActiveCall((prev) => ({
            status: 'connected',
            error: '',
            callId: prev.callId || normalized.callId,
            startedAt: prev.startedAt || Date.now(),
            endedAt: null,
            endedReason: '',
            callData: { ...(prev.callData || {}), ...(data || {}), sdpAnswer: remoteAnswerSdp },
          }));
        })
        .catch((error) => {
          console.error('Failed to apply remote answer on connected call event', error);
          markCallFailed(error?.message || 'Failed to connect outbound call');
        });
    },
    [applyRemoteAnswer, clearRingTimeout, markCallFailed, matchesActiveCall, updateActiveCall],
  );

  const handleStateEvent = useCallback(
    (data) => {
      if (!matchesActiveCall(data)) return;

      const normalized = normalizeCallData(data);
      const derivedStatus = getEventStatus(data);

      if (derivedStatus && derivedStatus !== 'disconnected') {
        const statusError =
          derivedStatus === 'busy'
            ? 'User is busy'
            : derivedStatus === 'no-answer'
            ? 'No answer from user'
            : derivedStatus === 'missed'
            ? 'Missed call'
            : normalized.reason || 'Call failed';

        markCallEnded({
          status: derivedStatus,
          error: statusError,
          reason: normalized.event || normalized.status || derivedStatus,
          data,
        });
        return true;
      }

      return false;
    },
    [markCallEnded, matchesActiveCall],
  );

  const handleTerminatedCall = useCallback(
    (data) => {
      if (!matchesActiveCall(data)) {
        return;
      }

      const normalized = normalizeCallData(data);
      markCallEnded({
        status: getEventStatus(data) || 'disconnected',
        error: '',
        reason: normalized.event || normalized.status || 'terminated',
        data,
      });
    },
    [markCallEnded, matchesActiveCall],
  );

  useEffect(() => {
    if (!activeCall.isOpen || activeCall.direction !== 'outgoing') {
      clearRingTimeout();
      return undefined;
    }

    if (!['calling', 'ringing', 'initiating'].includes(activeCall.status)) {
      clearRingTimeout();
      return undefined;
    }

    timeoutRef.current = window.setTimeout(() => {
      markCallEnded({
        status: 'no-answer',
        error: 'No answer from user',
        reason: 'ring-timeout',
      });
    }, RING_TIMEOUT_MS);

    return () => clearRingTimeout();
  }, [activeCall.direction, activeCall.isOpen, activeCall.status, clearRingTimeout, markCallEnded]);

  useEffect(() => {
    const handleOffline = () => {
      if (!activeCallRef.current?.isOpen) return;

      markCallEnded({
        status: 'disconnected',
        error: 'Network connection lost',
        reason: 'network-offline',
      });
    };

    window.addEventListener('offline', handleOffline);
    return () => window.removeEventListener('offline', handleOffline);
  }, [markCallEnded]);

  useEffect(() => {
    if (!companyId || !selectedMetadataId) return;

    if (callSocketRef.current) {
      callSocketRef.current.disconnect();
      callSocketRef.current = null;
    }

    const callSocket = io(WEBSOCKET_CONFIG.SERVER_URL, {
      path: WEBSOCKET_CONFIG.PATH,
      transports: WEBSOCKET_CONFIG.TRANSPORTS,
      forceNew: true,
      autoConnect: true,
      timeout: 20000,
      query: {
        request: `companyId:${companyId}&metadataId:${selectedMetadataId}`,
      },
    });

    callSocketRef.current = callSocket;

    callSocket.on('call_connect', (data) => {
      logCallDebug('socket:call_connect', data);
      if (matchesActiveCall(data) && activeCallRef.current?.direction === 'outgoing') {
        handleConnectedCall(data);
        return;
      }

      const incomingPhone = getCallPhone(data);
      if (!activeCallRef.current?.isOpen && incomingPhone) {
        openIncomingCall(data);
      }
    });

    callSocket.on('call_ringing', (data) => {
      logCallDebug('socket:call_ringing', data);
      if (!matchesActiveCall(data) || handleStateEvent(data)) return;

      updateActiveCall((prev) => ({
        status: 'ringing',
        error: '',
        callId: prev.callId || getCallId(data),
        callData: { ...(prev.callData || {}), ...(data || {}) },
      }));
    });

    callSocket.on('call_connected', (data) => {
      logCallDebug('socket:call_connected', data);
      handleConnectedCall(data);
    });
    callSocket.on('call_ended', (data) => {
      logCallDebug('socket:call_ended', data);
      handleTerminatedCall(data);
    });
    callSocket.on('call_terminate', (data) => {
      logCallDebug('socket:call_terminate', data);
      handleTerminatedCall(data);
    });

    callSocket.on('new_message', (data) => {
      logCallDebug('socket:new_message', data);
      if (data?.type !== 'outbound-call') return;

      const eventStatus = getEventStatus(data);
      if (['busy', 'no-answer', 'missed'].includes(eventStatus)) {
        handleStateEvent(data);
        return;
      }

      if (eventStatus === 'disconnected') {
        handleTerminatedCall(data);
        return;
      }

      const normalized = normalizeCallData(data);
      if (normalized.event === 'connect') {
        handleConnectedCall(data);
      }
    });

    callSocket.on('connect', () => {
      console.log('Connected to call WebSocket');
    });

    callSocket.on('disconnect', (reason) => {
      console.log('Disconnected from call WebSocket');

      if (activeCallRef.current?.isOpen && !TERMINAL_STATUSES.has(activeCallRef.current.status)) {
        markCallEnded({
          status: 'disconnected',
          error: 'Call socket disconnected',
          reason: reason || 'socket-disconnect',
        });
      }
    });

    callSocket.on('connect_error', (error) => {
      console.error('Call socket connection error', error);

      if (activeCallRef.current?.isOpen && !TERMINAL_STATUSES.has(activeCallRef.current.status)) {
        markCallEnded({
          status: 'failed',
          error: error?.message || 'Unable to connect call socket',
          reason: 'socket-connect-error',
        });
      }
    });

    const handleBeforeUnload = () => {
      if (callSocketRef.current) {
        callSocketRef.current.disconnect();
        callSocketRef.current = null;
      }
      disconnectPeer();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearRingTimeout();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      callSocket.disconnect();
      callSocketRef.current = null;
    };
  }, [
    clearRingTimeout,
    companyId,
    disconnectPeer,
    handleConnectedCall,
    handleStateEvent,
    handleTerminatedCall,
    markCallEnded,
    matchesActiveCall,
    openIncomingCall,
    selectedMetadataId,
    updateActiveCall,
  ]);

  const acceptCall = () => {
    updateActiveCall({ status: 'connected', error: '', startedAt: Date.now() });
  };

  const declineCall = () => {
    clearActiveCall();
  };

  const closeCall = () => {
    clearActiveCall();
  };

  const value = {
    activeCall,
    acceptCall,
    declineCall,
    closeCall,
    updateActiveCall,
    startOutgoingCall,
    createOutgoingOffer,
    createIncomingAnswer,
    applyRemoteAnswer,
    emitCallEvent,
    getSocketId,
    disconnectPeer,
    toggleMute: () => {
      if (!webRtcRef.current) return false;

      if (webRtcRef.current.isAudioMuted()) {
        webRtcRef.current.unmuteAudio();
        return false;
      }

      webRtcRef.current.muteAudio();
      return true;
    },
    getIsMuted: () => {
      if (!webRtcRef.current) return false;
      return webRtcRef.current.isAudioMuted();
    },
    toggleHold: () => {
      if (webRtcRef.current && typeof webRtcRef.current.toggleHold === 'function') {
        webRtcRef.current.toggleHold();
      }
    },
    getConnectionState: () => webRtcRef.current?.getConnectionState?.() || 'new',
    getOfferSdp,
    getRemoteAnswerSdp,
    markCallFailed,
    clearActiveCall,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
};
